/**
 * Calculate total carbon emissions from travel sections
 * @param {Array<TravelSection>} travelSections - Array of travel sections
 * @returns {number | null} - Total carbon emissions in kg or null if no data
 */
export function calculateTotalEmissions(travelSections: TravelSections): number | null | undefined {
  if (!travelSections || !Array.isArray(travelSections) || travelSections.length === 0) {
    return null;
  }

  let total = 0;
  let hasAtLeastOneValue = false;
  travelSections.forEach((section) => {
    const emission = section?.transport?.carbonEmission;

    if (typeof emission === 'number' && !Number.isNaN(emission)) {
      total += emission;
      hasAtLeastOneValue = true;
    }
  });
  return hasAtLeastOneValue ? total : null;
}

/**
 * Calculate average carbon emissions from travel sections
 * @param {Array<TravelSection>} travelSections - Array of travel sections
 * @returns {number | null} - Average carbon emissions in kg or null if no data
 */
export function calculateAverageEmissions(
  travelSections: TravelSections,
): number | null | undefined {
  if (!travelSections || !Array.isArray(travelSections) || travelSections.length === 0) {
    return null;
  }

  let total = 0;
  let hasAtLeastOneValue = false;
  travelSections.forEach((section) => {
    const emission = section?.transport?.averageCarbonEmission;

    if (typeof emission === 'number' && !Number.isNaN(emission)) {
      total += emission;
      hasAtLeastOneValue = true;
    }
  });
  return hasAtLeastOneValue ? total : null;
}

/**
 * Calculate percentage difference between actual and average emissions
 * @param {number} total - Total actual carbon emissions
 * @param {number} average - Average carbon emissions
 * @returns {{percentage: number, isLess: boolean} | null} - Percentage difference with sign or null
 */
export function calculatePercentageDifference(
  total: number | null | undefined,
  average: number | null | undefined,
):
  | {
      percentage: number;
      isLess: boolean;
    }
  | null
  | undefined {
  if (
    typeof total !== 'number' ||
    typeof average !== 'number' ||
    Number.isNaN(total) ||
    Number.isNaN(average) ||
    average === 0
  ) {
    return null;
  }

  const difference = ((total - average) / average) * 100;
  const percentage = Math.abs(Math.round(difference));
  const isLess = difference < 0;
  return {
    percentage,
    isLess,
  };
}

/**
 * Check if travel sections have any carbon emission data
 * @param {Array<TravelSection>} travelSections - Array of travel sections
 * @returns {boolean} - True if at least one section has carbon emission data
 */
export function hasCarbonEmissionData(travelSections: TravelSections): boolean {
  if (!travelSections || !Array.isArray(travelSections)) {
    return false;
  }

  return travelSections.some((section) => {
    const emission = section?.transport?.carbonEmission;
    return typeof emission === 'number' && !Number.isNaN(emission);
  });
}
